import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Grid,
  Divider,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  // Paper,
  List,
  ListItem,
  ListItemText,
  IconButton
  // Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Assignment,
  // TrendingUp,
  CheckCircle,
  Warning,
  // Timer,
  // Person,
  // CalendarToday,
  // Security,
  Description,
  Add
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

// interface Incident {
//   id: number;
//   incident_id: string;
//   status: string;
//   severity: string;
//   violation_type: string;
//   security_rule_name: string;
//   compliance_framework: string;
//   violation_details: any;
//   assignee_id: number | null;
//   created_at: string;
//   sla_deadline: string | null;
//   sla_breach: boolean;
//   root_cause_category: string | null;
//   root_cause_explanation: string | null;
// }

const IncidentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [rootCauseDialogOpen, setRootCauseDialogOpen] = useState(false);

  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [noteText, setNoteText] = useState('');
  const [actionDescription, setActionDescription] = useState('');
  const [actionType, setActionType] = useState('remediation');
  const [rootCauseCategory, setRootCauseCategory] = useState('');
  const [rootCauseExplanation, setRootCauseExplanation] = useState('');

  const {
    data: incident,
    isLoading,
    error
  } = useQuery({
    queryKey: ['incident', id],
    queryFn: async () => {
      const response = await axios.get(`/api/incidents/${id}`);
      return response.data.data;
    }
  });

  const { data: timeline } = useQuery({
    queryKey: ['incidentTimeline', id],
    queryFn: async () => {
      const response = await axios.get(`/api/incidents/${id}/timeline`);
      return response.data.data;
    }
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (data: any) => {
      return axios.patch(`/api/incidents/${id}/status`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident', id] });
      queryClient.invalidateQueries({ queryKey: ['incidentTimeline', id] });
      setStatusDialogOpen(false);
      setStatusNote('');
    }
  });

  const assignMutation = useMutation({
    mutationFn: async (data: any) => {
      return axios.patch(`/api/incidents/${id}/assign`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident', id] });
      queryClient.invalidateQueries({ queryKey: ['incidentTimeline', id] });
      setAssignDialogOpen(false);
    }
  });

  const addNoteMutation = useMutation({
    mutationFn: async (data: any) => {
      return axios.post(`/api/incidents/${id}/notes`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidentTimeline', id] });
      setNoteDialogOpen(false);
      setNoteText('');
    }
  });

  const addActionMutation = useMutation({
    mutationFn: async (data: any) => {
      return axios.post(`/api/incidents/${id}/actions`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidentTimeline', id] });
      setActionDialogOpen(false);
      setActionDescription('');
    }
  });

  const documentRootCauseMutation = useMutation({
    mutationFn: async (data: any) => {
      return axios.post(`/api/incidents/${id}/root-cause`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident', id] });
      queryClient.invalidateQueries({ queryKey: ['incidentTimeline', id] });
      setRootCauseDialogOpen(false);
      setRootCauseCategory('');
      setRootCauseExplanation('');
    }
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'error';
      case 'assigned':
        return 'warning';
      case 'investigating':
        return 'info';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleUpdateStatus = () => {
    updateStatusMutation.mutate({
      status: newStatus,
      note: statusNote
    });
  };

  const handleAssign = () => {
    assignMutation.mutate({
      assignee_id: parseInt(assigneeId)
    });
  };

  const handleAddNote = () => {
    addNoteMutation.mutate({
      note_text: noteText
    });
  };

  const handleAddAction = () => {
    addActionMutation.mutate({
      description: actionDescription,
      action_type: actionType
    });
  };

  const handleDocumentRootCause = () => {
    documentRootCauseMutation.mutate({
      category: rootCauseCategory,
      explanation: rootCauseExplanation
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !incident) {
    return <Alert severity="error">Failed to load incident details. Please try again.</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/incidents')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {incident.incident_id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {incident.violation_type}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Assignment />}
            onClick={() => setAssignDialogOpen(true)}
          >
            Assign
          </Button>
          <Button variant="outlined" startIcon={<Edit />} onClick={() => setStatusDialogOpen(true)}>
            Update Status
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Info */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Incident Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={incident.status.toUpperCase()}
                      color={getStatusColor(incident.status) as any}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Severity
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={incident.severity.toUpperCase()}
                      color={getSeverityColor(incident.severity) as any}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Security Rule
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    {incident.security_rule_name || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Framework
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    {incident.compliance_framework || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    {format(new Date(incident.created_at), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    SLA Status
                  </Typography>
                  <Box mt={0.5}>
                    {incident.sla_breach ? (
                      <Chip icon={<Warning />} label="BREACHED" size="small" color="error" />
                    ) : (
                      <Chip label="ON TRACK" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Root Cause */}
          {incident.root_cause_category ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Root Cause Analysis
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Chip
                  label={incident.root_cause_category.replace('_', ' ').toUpperCase()}
                  size="small"
                  color="primary"
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2">{incident.root_cause_explanation}</Typography>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Root Cause Analysis</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setRootCauseDialogOpen(true)}
                  >
                    Document Root Cause
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" mt={2}>
                  No root cause has been documented yet.
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Timeline</Typography>
                <Box display="flex" gap={1}>
                  <Button size="small" startIcon={<Add />} onClick={() => setNoteDialogOpen(true)}>
                    Add Note
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setActionDialogOpen(true)}
                  >
                    Add Action
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <List>
                {timeline?.map((item: any, index: number) => (
                  <ListItem key={index} alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight="medium">
                            {item.type === 'note' ? 'Note' : 'Action'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(item.timestamp), 'MMM dd, yyyy HH:mm')}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" mt={0.5}>
                          {item.text}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexDirection="column" gap={1}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Edit />}
                  onClick={() => setStatusDialogOpen(true)}
                >
                  Update Status
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Assignment />}
                  onClick={() => setAssignDialogOpen(true)}
                >
                  Assign
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Description />}
                  onClick={() => setNoteDialogOpen(true)}
                >
                  Add Note
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<CheckCircle />}
                  onClick={() => setActionDialogOpen(true)}
                >
                  Add Action
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="New Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            fullWidth
            margin="normal"
          >
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="investigating">Investigating</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
          <TextField
            label="Note (optional)"
            value={statusNote}
            onChange={(e) => setStatusNote(e.target.value)}
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained" disabled={!newStatus}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Incident</DialogTitle>
        <DialogContent>
          <TextField
            label="Assignee User ID"
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            fullWidth
            margin="normal"
            type="number"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssign} variant="contained" disabled={!assigneeId}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            label="Note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            fullWidth
            multiline
            rows={4}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddNote} variant="contained" disabled={!noteText}>
            Add Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Action Item</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Action Type"
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
            fullWidth
            margin="normal"
          >
            <MenuItem value="remediation">Remediation</MenuItem>
            <MenuItem value="investigation">Investigation</MenuItem>
            <MenuItem value="communication">Communication</MenuItem>
            <MenuItem value="documentation">Documentation</MenuItem>
            <MenuItem value="prevention">Prevention</MenuItem>
          </TextField>
          <TextField
            label="Description"
            value={actionDescription}
            onChange={(e) => setActionDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAction} variant="contained" disabled={!actionDescription}>
            Add Action
          </Button>
        </DialogActions>
      </Dialog>

      {/* Root Cause Dialog */}
      <Dialog
        open={rootCauseDialogOpen}
        onClose={() => setRootCauseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Document Root Cause</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Category"
            value={rootCauseCategory}
            onChange={(e) => setRootCauseCategory(e.target.value)}
            fullWidth
            margin="normal"
          >
            <MenuItem value="configuration_error">Configuration Error</MenuItem>
            <MenuItem value="user_error">User Error</MenuItem>
            <MenuItem value="system_bug">System Bug</MenuItem>
            <MenuItem value="external_attack">External Attack</MenuItem>
            <MenuItem value="false_positive">False Positive</MenuItem>
          </TextField>
          <TextField
            label="Explanation (minimum 50 characters)"
            value={rootCauseExplanation}
            onChange={(e) => setRootCauseExplanation(e.target.value)}
            fullWidth
            multiline
            rows={6}
            margin="normal"
            helperText={`${rootCauseExplanation.length} / 50 minimum characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRootCauseDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDocumentRootCause}
            variant="contained"
            disabled={!rootCauseCategory || rootCauseExplanation.length < 50}
          >
            Document
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncidentDetails;
